/**
 * The "Standardized Procedure Certifications" section of the Certification & Compliance page
 * (`src/pages/why-ryerson/ryerson-commitment/certification-compliance/index.tsx`, its only
 * importer): the Quality Management System blurb and one card per certificate (ISO 9001 for the
 * US and Canada, Mexico, Fay and the two Laserflex sites, and the TSP ASME certificate), each
 * linking to its PDF. Copy and file names are in `./Content.tsx`; layouts in `./Desktop/` and
 * `./Mobile/`.
 */
import React from "react";
import { Media } from "@ryerson/frontend.next";
import { StandardizedProcedureCertificationsContent } from "./Content";
import StandardizedProcedureCertificationsDesktop from "./Desktop/StandardizedProcedureCertificationsDesktop";
import StandardizedProcedureCertificationsMobile from "./Mobile/StandardizedProcedureCertificationsMobile";
import { useApplication } from "@ryerson/frontend.application";

/** One certificate card. */
export type StandardizedProcedureCertificationsSheet = {
	/** Link text ("Download …"). */
	caption: string;
	/** Certificate name: the card heading on desktop, the image alt text on mobile. */
	name: string;
	/**
	 * PDF file name under `/static-assets/documents/pdfs/<language>/compliance-certifications/`.
	 * The file must exist in the en, fr and es folders of the web-static-assets repository.
	 */
	url: string;
	/** Site-relative preview image under `application/public/images/`. */
	imageUrl: string;
};

/** The section copy for one language. */
export interface StandardizedProcedureCertificationsProps {
	/** Section heading; the page also uses it as the `AllSections` title and anchor. */
	title: string;
	description: string;
	/** First row of cards on desktop (one row, no wrapping); mobile wraps each row's cards. */
	standardizedProcedureCertificationsSheets: StandardizedProcedureCertificationsSheet[];
	/** Second row of cards, laid out the same way. */
	standardizedProcedureCertificationsSheetsRow2: StandardizedProcedureCertificationsSheet[];
}

/** Renders the section in the visitor's language, mobile below `lg`. */
const StandardizedProcedureCertifications: React.FC = () => {
	const {
		localization: { language },
	} = useApplication();
	const content = StandardizedProcedureCertificationsContent[language];

	return (
		<>
			<Media lessThan="lg">
				<StandardizedProcedureCertificationsMobile {...content} />
			</Media>
			<Media greaterThanOrEqual="lg">
				<StandardizedProcedureCertificationsDesktop {...content} />
			</Media>
		</>
	);
};

export default StandardizedProcedureCertifications;
